import { Activity, forwardRef, memo, type FormEvent, useCallback } from "react";
import type { ISearchCategory } from "../../../../constants/searchCategory";
import { SearchCategorySelector } from "../searchCategorySelector/searchCategorySelector";
import { SubmitSearchBtn } from "../submitSearchBtn/submitSearchBtn";
import { SearchResultsDropdown } from "../searchResultsDropdown/searchResultsDropdown";
import type { SearchResults } from "../../../../apiClients/search/interfaces";
import { useSearchDropdown } from "../hooks/useSearchDropdown";
import styles from "./searchContent.module.css";

export type SearchContentProps = {
  selectedCategory: ISearchCategory;
  onSelectCategory: (category: ISearchCategory) => void;
  query: string;
  onQueryChange: (value: string) => void;
  onAfterNavigate?: () => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  inputId: string;
  searchPlaceholder: string;
  searchResults: SearchResults | null;
  isSearching: boolean;
  searchError: Error | null;
  showResults?: boolean;
};

export const SearchContent = memo(
  forwardRef<HTMLInputElement, SearchContentProps>(function SearchContent(
    {
      selectedCategory,
      onSelectCategory,
      query,
      onQueryChange,
      onAfterNavigate,
      onSubmit,
      inputId,
      searchPlaceholder,
      searchResults,
      isSearching,
      searchError,
      showResults = true,
    },
    inputRef,
  ) {
    const handleSubmit = useCallback(
      (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit?.(event);
      },
      [onSubmit],
    );

    const handleResultSelect = useCallback(() => {
      onAfterNavigate?.();
    }, [onAfterNavigate]);

    const hasResults = Boolean(
      searchResults &&
      Object.values(searchResults).some((arr) => arr.length > 0),
    );
    const hasError = Boolean(searchError);
    const hasSearchResults = Boolean(searchResults);

    const { isOpen, containerRef, dropdownRef } = useSearchDropdown({
      hasResults: showResults && (hasResults || hasSearchResults),
      isSearching: showResults && isSearching,
      hasError: showResults && hasError,
    });

    const shouldShowDropdown =
      isOpen && showResults && (searchResults || isSearching || searchError);

    return (
      <div className={styles.searchWrapper} ref={containerRef}>
        <form className={styles.searchContent} onSubmit={handleSubmit}>
          <SearchCategorySelector
            selectedCategory={selectedCategory}
            onSelect={onSelectCategory}
          />
          <input
            id={inputId}
            className={styles.searchInput}
            name="search"
            type="search"
            placeholder={searchPlaceholder}
            ref={inputRef}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            autoComplete="off"
          />
          <SubmitSearchBtn onAfterNavigate={onAfterNavigate} />
        </form>

        <Activity mode={shouldShowDropdown ? "visible" : "hidden"}>
          <SearchResultsDropdown
            ref={dropdownRef}
            results={searchResults}
            isSearching={isSearching}
            error={searchError}
            onSelectResult={handleResultSelect}
          />
        </Activity>
      </div>
    );
  }),
);

SearchContent.displayName = "SearchContent";
